class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleVariantTriggerClick = this.handleVariantTriggerClick.bind(this);
    this.handleCardMouseEnter = this.handleCardMouseEnter.bind(this);
    this.handleCardMouseLeave = this.handleCardMouseLeave.bind(this);
    this.handleCardFocusIn = this.handleCardFocusIn.bind(this);
    this.handleCardFocusOut = this.handleCardFocusOut.bind(this);
    this.handleWindowLoad = this.handleWindowLoad.bind(this);
    this.handleHoverImageIntersection = this.handleHoverImageIntersection.bind(this);
  }

  connectedCallback() {
    if (this._listenerAdded) return;
    this._listenerAdded = true;

    this.forms = Array.from(this.querySelectorAll('form[action]'));
    this.variantSelect = this.querySelector('[data-card4-variant-select]');
    this.variantTriggers = Array.from(this.querySelectorAll('[data-card4-variant-trigger]'));
    this.moreLink = this.querySelector('[data-card4-more-link]');
    this.priceElement = this.querySelector('[data-card4-price]');
    this.compareElement = this.querySelector('[data-card4-compare]');
    this.compareValueElement = this.querySelector('[data-card4-compare-value]');
    this.mediaElement = this.querySelector('[data-product-card-media]');
    this.imageHoverTarget = this.mediaElement?.closest('a') || this.mediaElement;
    this.imageElement = this.mediaElement?.querySelector('img') || null;
    this.defaultImageState = this.imageElement
      ? {
          src: this.imageElement.getAttribute('src') || '',
          srcset: this.imageElement.getAttribute('srcset') || '',
          alt: this.imageElement.getAttribute('alt') || ''
        }
      : null;
    this.hoverImageSrc = this.mediaElement?.dataset.productCardHoverImage || '';
    this.hoverImageAlt =
      this.mediaElement?.dataset.productCardHoverImageAlt || this.defaultImageState?.alt || '';
    this.hoverImageElement = null;
    this.hoverImageLoadPromise = null;
    this.hoverImageFailed = false;
    this.isHoverImageActive = false;
    this.enableVariantImageSwap = this.dataset.card4VariantImageEnabled === 'true';
    this.hasHoverImageSupport = Boolean(this.mediaElement && this.imageElement && this.hoverImageSrc);

    this.forms.forEach((form) => {
      form.addEventListener('submit', this.handleFormSubmit);
    });

    this.variantTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleVariantTriggerClick);
    });

    if (this.hasHoverImageSupport) {
      this.mediaElement.classList.add('has-hover-image');
      this.mediaElement.addEventListener('mouseenter', this.handleCardMouseEnter);
      this.mediaElement.addEventListener('mouseleave', this.handleCardMouseLeave);
      this.imageHoverTarget?.addEventListener('focusin', this.handleCardFocusIn);
      this.imageHoverTarget?.addEventListener('focusout', this.handleCardFocusOut);
      this.setupHoverImagePreload();
    }

    if (this.variantSelect) {
      const initialVariant = this.getSelectedVariantOption();
      if (initialVariant) {
        this.syncVariantState(initialVariant, { updateImage: false });
      }
    }
  }

  disconnectedCallback() {
    if (!this._listenerAdded) return;

    this.forms?.forEach((form) => {
      form.removeEventListener('submit', this.handleFormSubmit);
    });

    this.variantTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleVariantTriggerClick);
    });

    if (this.hasHoverImageSupport) {
      this.mediaElement?.removeEventListener('mouseenter', this.handleCardMouseEnter);
      this.mediaElement?.removeEventListener('mouseleave', this.handleCardMouseLeave);
      this.imageHoverTarget?.removeEventListener('focusin', this.handleCardFocusIn);
      this.imageHoverTarget?.removeEventListener('focusout', this.handleCardFocusOut);
      window.removeEventListener('load', this.handleWindowLoad);
      this.destroyHoverImageObserver();
      this.cancelHoverImagePreload();
    }

    this._listenerAdded = false;
  }

  getSelectedVariantOption() {
    if (!this.variantSelect) return null;

    return (
      this.variantSelect.selectedOptions?.[0] ||
      this.variantSelect.querySelector('option[selected]') ||
      this.variantSelect.options?.[0] ||
      null
    );
  }

  handleVariantTriggerClick(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    if (trigger?.disabled) return;
    this.selectVariantById(trigger.dataset.variantId);
  }

  handleCardMouseEnter() {
    this.showHoverImage();
  }

  handleCardMouseLeave() {
    this.hideHoverImage();
  }

  handleCardFocusIn() {
    this.showHoverImage();
  }

  handleCardFocusOut(event) {
    if (event.relatedTarget && this.imageHoverTarget?.contains(event.relatedTarget)) return;
    this.hideHoverImage();
  }

  handleWindowLoad() {
    this.observeHoverImageViewport();
  }

  handleHoverImageIntersection(entries) {
    const isVisible = entries.some((entry) => entry.isIntersecting);
    if (!isVisible) return;

    this.destroyHoverImageObserver();
    this.queueHoverImagePreload();
  }

  selectVariantById(variantId) {
    if (!this.variantSelect || !variantId) return;

    const variantOption = Array.from(this.variantSelect.options).find((option) => {
      return option.value === variantId || option.dataset.variantId === variantId;
    });

    if (!variantOption || variantOption.disabled) return;

    this.variantSelect.value = variantOption.value;
    variantOption.selected = true;
    this.hideHoverImage();
    this.syncVariantState(variantOption);
  }

  syncVariantState(variantOption, { updateImage = this.enableVariantImageSwap } = {}) {
    if (updateImage) {
      this.updateImage(variantOption);
    }

    this.updatePrice(variantOption);
    this.updateForms(variantOption);
    this.updateVariantButtons(variantOption);
  }

  updateImage(variantOption) {
    if (!this.enableVariantImageSwap || !this.imageElement) return;

    const variantImage = variantOption.dataset.variantImage || '';
    const variantImageAlt = variantOption.dataset.variantImageAlt || this.defaultImageState?.alt || '';

    if (variantImage) {
      this.imageElement.src = variantImage;
      this.imageElement.srcset = '';
      this.imageElement.alt = variantImageAlt;
      return;
    }

    if (!this.defaultImageState) return;

    this.imageElement.src = this.defaultImageState.src;
    this.imageElement.srcset = this.defaultImageState.srcset;
    this.imageElement.alt = this.defaultImageState.alt;
  }

  showHoverImage() {
    if (!this.hasHoverImageSupport || this.hoverImageFailed) return;

    this.isHoverImageActive = true;
    this.ensureHoverImage().then((hoverImage) => {
      if (!hoverImage || !this.isHoverImageActive) return;
      this.mediaElement?.classList.add('is-hover-image-visible');
    });
  }

  hideHoverImage() {
    this.isHoverImageActive = false;
    this.mediaElement?.classList.remove('is-hover-image-visible');
  }

  setupHoverImagePreload() {
    if (!this.hasHoverImageSupport || this.hoverImageElement || this.hoverImageFailed) return;

    if (document.readyState === 'complete') {
      this.observeHoverImageViewport();
      return;
    }

    window.addEventListener('load', this.handleWindowLoad, { once: true });
  }

  observeHoverImageViewport() {
    if (
      !this.hasHoverImageSupport ||
      this.hoverImageElement ||
      this.hoverImageFailed ||
      !('IntersectionObserver' in window)
    ) {
      return;
    }

    if (this.hoverImageObserver) return;

    this.hoverImageObserver = new IntersectionObserver(this.handleHoverImageIntersection, {
      threshold: 0.01
    });

    this.hoverImageObserver.observe(this);
  }

  destroyHoverImageObserver() {
    if (!this.hoverImageObserver) return;

    this.hoverImageObserver.disconnect();
    this.hoverImageObserver = null;
  }

  queueHoverImagePreload() {
    if (!this.hasHoverImageSupport || this.hoverImageElement || this.hoverImageFailed) return;
    if (this.hoverImageIdleCallback || this.hoverImagePreloadTimer) return;

    const preload = () => {
      this.hoverImageIdleCallback = null;
      this.hoverImagePreloadTimer = null;
      this.ensureHoverImage();
    };

    if ('requestIdleCallback' in window) {
      this.hoverImageIdleCallback = window.requestIdleCallback(preload, { timeout: 1200 });
      return;
    }

    this.hoverImagePreloadTimer = window.setTimeout(preload, 150);
  }

  cancelHoverImagePreload() {
    if (this.hoverImageIdleCallback && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.hoverImageIdleCallback);
    }

    if (this.hoverImagePreloadTimer) {
      window.clearTimeout(this.hoverImagePreloadTimer);
    }

    this.hoverImageIdleCallback = null;
    this.hoverImagePreloadTimer = null;
  }

  ensureHoverImage() {
    if (!this.hasHoverImageSupport || this.hoverImageFailed) {
      return Promise.resolve(null);
    }

    if (this.hoverImageElement) {
      return this.hoverImageLoadPromise || Promise.resolve(this.hoverImageElement);
    }

    const hoverImage = document.createElement('img');
    const width = this.imageElement?.getAttribute('width');
    const height = this.imageElement?.getAttribute('height');

    hoverImage.className = 'product-card-hover-image';
    hoverImage.alt = this.hoverImageAlt;
    hoverImage.decoding = 'async';
    hoverImage.loading = 'eager';
    hoverImage.setAttribute('aria-hidden', 'true');

    if (width) {
      hoverImage.setAttribute('width', width);
    }

    if (height) {
      hoverImage.setAttribute('height', height);
    }

    this.hoverImageElement = hoverImage;
    this.hoverImageLoadPromise = new Promise((resolve) => {
      hoverImage.addEventListener('load', () => resolve(hoverImage), { once: true });
      hoverImage.addEventListener('error', () => {
        this.hoverImageFailed = true;
        this.mediaElement?.classList.remove('has-hover-image');
        hoverImage.remove();
        this.hoverImageElement = null;
        this.hoverImageLoadPromise = null;
        resolve(null);
      }, { once: true });
    });

    this.mediaElement.append(hoverImage);
    hoverImage.src = this.hoverImageSrc;

    return this.hoverImageLoadPromise;
  }

  updatePrice(variantOption) {
    if (!this.priceElement) return;

    const price = Number.parseInt(variantOption.dataset.variantPrice, 10) || 0;
    const comparePrice = Number.parseInt(variantOption.dataset.variantComparePrice, 10);
    const priceFormatted =
      variantOption.dataset.variantPriceFormatted || this.formatMoney(price);
    const compareFormatted =
      variantOption.dataset.variantComparePriceFormatted || this.formatMoney(comparePrice);
    const hasComparePrice = Number.isFinite(comparePrice) && comparePrice > price;

    this.priceElement.textContent = priceFormatted;

    if (!this.compareElement) return;

    this.compareElement.hidden = !hasComparePrice;
    if (hasComparePrice && this.compareValueElement) {
      this.compareValueElement.textContent = compareFormatted;
    }
  }

  updateForms(variantOption) {
    const variantId = variantOption.dataset.variantId || variantOption.value;
    const available = variantOption.dataset.variantAvailable === 'true';

    this.forms.forEach((form) => {
      const action = form.dataset.productCardAction || 'cart';
      const idInput = form.querySelector('input[name="id"]');
      const submitBtn = form.querySelector('button[type="submit"]');

      if (idInput) {
        idInput.value = variantId;
      }

      form.dataset.variantId = variantId;
      form.dataset.inventoryQuantity = variantOption.dataset.variantInventory || '0';
      form.dataset.inventoryManagement = variantOption.dataset.variantInventoryManagement || '';
      form.dataset.inventoryPolicy = variantOption.dataset.variantInventoryPolicy || '';
      form.hidden = action === 'checkout' && !available;

      if (!submitBtn) return;

      const text = submitBtn.querySelector('.btn-text');
      const availableText = submitBtn.dataset.availableText || text?.textContent || '';
      const soldOutText = submitBtn.dataset.soldOutText || availableText;

      if (text) {
        text.textContent = available ? availableText : soldOutText;
      }

      submitBtn.disabled = !available;
    });
  }

  updateVariantButtons(variantOption) {
    const selectedId = variantOption.dataset.variantId || variantOption.value;
    let hasVisibleActive = false;

    this.variantTriggers.forEach((trigger) => {
      const isActive = trigger.dataset.variantId === selectedId;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      hasVisibleActive = hasVisibleActive || isActive;
    });

    if (this.moreLink) {
      this.moreLink.classList.toggle('is-active', !hasVisibleActive);
    }
  }

  formatMoney(cents) {
    if (window.Money?.format) {
      return window.Money.format(cents);
    }

    const safeValue = Number.isFinite(cents) ? cents : 0;
    const symbol = window.theme?.currencySymbol || '$';
    return `${symbol}${(safeValue / 100).toFixed(2)}`;
  }

  async handleFormSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const submitBtn = form.querySelector('button[type="submit"]');

    if (!submitBtn || submitBtn.disabled) return;

    submitBtn.setAttribute('disabled', 'disabled');
    submitBtn.classList.add('loading');

    const formData = new FormData(form);
    const variantData = this.getSelectedVariantOption() || form;
    const action = form.dataset.productCardAction || 'cart';

    try {
      if (action === 'checkout') {
        await this.buyNow(formData, variantData);
        return;
      }

      const cartResponse = await this.submitToCart(formData, variantData);
      const cart = window.CartApi?.refreshCart
        ? await CartApi.refreshCart(cartResponse)
        : cartResponse;

      document.dispatchEvent(new CustomEvent('cart:added', { detail: cart || cartResponse }));

      if (!window.SimpleHeaderCart?.open) {
        const cartTrigger =
          document.getElementById('simple-header-cart-trigger') ||
          document.getElementById('custom-header-cart-trigger');
        if (cartTrigger) {
          cartTrigger.click();
        }
      }
    } catch (error) {
      if (window.CartApi?.showToast) {
        CartApi.showToast(error.message, true);
      } else {
        console.error(error);
      }
    } finally {
      submitBtn.classList.remove('loading');

      const selectedVariant = this.getSelectedVariantOption();
      const isAvailable =
        !selectedVariant || selectedVariant.dataset.variantAvailable === 'true';

      if (isAvailable) {
        submitBtn.removeAttribute('disabled');
      } else {
        submitBtn.setAttribute('disabled', 'disabled');
      }
    }
  }

  async submitToCart(formData, variantData) {
    if (window.CartApi?.addToCart) {
      return CartApi.addToCart(formData, { variantData });
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Could not add to cart');
    }

    return response.json();
  }

  async buyNow(formData, variantData) {
    await this.submitToCart(formData, variantData);

    const rootPath = window.Shopify?.routes?.root || '/';
    window.location.assign(new URL('checkout', `${window.location.origin}${rootPath}`).toString());
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
